import React, { useMemo, useRef, useState, useEffect } from "react";
import { BellRinging } from "@phosphor-icons/react";

/**
 * Iter209dw — Alert scadenze in card immobile.
 *
 * Mostra un'icona a campana rossa animata (vibrate) in basso a destra
 * SOLO quando almeno una scadenza nel campo `property.scadenze[]` cade
 * entro 15 giorni dalla data attuale (o e' gia scaduta e quindi urgente).
 *
 * Interazione:
 *  - Desktop: tooltip su mouseEnter/mouseLeave
 *  - Mobile:  tooltip su tap (toggle), chiude al tap-fuori
 *
 * Il tooltip mostra categoria, descrizione e data formattata in italiano
 * (es. "Mandato di vendita - scade il 15 ott 2026").
 */

const CATEGORY_LABELS = {
  scadenza_documenti_personali: "Documenti personali",
  scadenza_mandato_vendita: "Mandato di vendita",
  scadenza_compleanno: "Compleanno",
  scadenza_incarico: "Incarico",
  scadenza_vincoli_vendita: "Vincolo alla vendita",
};

const MONTHS_IT_SHORT = [
  "gen", "feb", "mar", "apr", "mag", "giu",
  "lug", "ago", "set", "ott", "nov", "dic",
];

function formatDateItShort(iso) {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const y = m[1];
  const month = MONTHS_IT_SHORT[parseInt(m[2], 10) - 1] || m[2];
  const d = parseInt(m[3], 10);
  return `${d} ${month} ${y}`;
}

function parseIsoDate(iso) {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
}

export default function PropertyDeadlineAlert({ property }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  const nextDeadline = useMemo(() => {
    const list = Array.isArray(property?.scadenze) ? property.scadenze : [];
    if (list.length === 0) return null;
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const limit = new Date(today.getTime() + 15 * 24 * 60 * 60 * 1000);
    const upcoming = list
      .map((s) => ({ ...s, _date: parseIsoDate(s.data_scadenza) }))
      .filter((s) => s._date && s._date <= limit)
      .sort((a, b) => a._date - b._date);
    return upcoming[0] || null;
  }, [property]);

  // Tap-outside su mobile: chiude il tooltip.
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [open]);

  if (!nextDeadline) return null;

  const label = CATEGORY_LABELS[nextDeadline.categoria] || "Scadenza";
  const dateStr = formatDateItShort(nextDeadline.data_scadenza);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const isPast = nextDeadline._date < today;
  const title = `${label} — ${isPast ? "scaduta il" : "scade il"} ${dateStr}`;

  const stop = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div
      ref={containerRef}
      className="absolute bottom-2 right-2 z-20"
      data-testid={`property-deadline-alert-${property._id}`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        onClick={(e) => { stop(e); setOpen((v) => !v); }}
        onTouchStart={stop}
        aria-label={title}
        className="w-8 h-8 rounded-full bg-red-500 text-white shadow-lg shadow-red-500/50 ring-2 ring-white flex items-center justify-center hover:bg-red-600 active:scale-95 transition"
        data-testid={`property-deadline-bell-${property._id}`}
      >
        <BellRinging size={16} weight="fill" className="vibrate-bell" />
      </button>
      {open && (
        <div
          className="absolute bottom-10 right-0 w-60 rounded-lg bg-slate-900 text-white text-xs px-3 py-2 shadow-xl ring-1 ring-slate-700 pointer-events-none"
          data-testid={`property-deadline-tooltip-${property._id}`}
          onClick={stop}
        >
          <div className="font-semibold text-red-300 mb-0.5">
            {label}
          </div>
          {nextDeadline.descrizione && (
            <div className="text-slate-200 leading-snug mb-1">
              {nextDeadline.descrizione}
            </div>
          )}
          <div className="text-slate-300">
            {isPast ? "scaduta il " : "scade il "}
            <strong className="text-white">{dateStr}</strong>
          </div>
        </div>
      )}
    </div>
  );
}
